import React, { useRef, useState, useEffect, type ReactNode } from "react"
import { ChevronLeft, ChevronRight } from "react-bootstrap-icons"
import { FeedSpinner } from "@components/shared/Spinner"
import styles from "./ExploreSection.module.css"

interface ExploreSectionProps<T> {
  title: string
  description?: string
  light?: boolean
  items: T[] | undefined
  isLoading: boolean
  keyExtractor: (item: T) => string | number
  renderItem: (item: T) => ReactNode
}

const SCROLL_AMOUNT = 420

export function ExploreSection<T>({
  title,
  description,
  light,
  items,
  isLoading,
  keyExtractor,
  renderItem,
}: ExploreSectionProps<T>) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const updateScrollState = () => {
    const el = scrollRef.current
    if (!el) return
    setCanScrollLeft(el.scrollLeft > 0)
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1)
  }

  useEffect(() => {
    updateScrollState()
  }, [items])

  const scrollBy = (amount: number) => {
    scrollRef.current?.scrollBy({ left: amount, behavior: "smooth" })
  }

  if (!isLoading && !items?.length) return null

  return (
    <div className="mt-8 px-3.5">
      <div
        className={`text-[13px] font-bold uppercase ${
          light ? "text-white" : "text-black-800"
        } ${description ? "mb-0.5" : "mb-2.5"}`}
      >
        {title}
      </div>
      {description && (
        <div
          className={`italic mb-2.5 text-xs ${
            light ? "text-white/80" : "text-gray-700"
          }`}
        >
          {description}
        </div>
      )}
      {isLoading ? (
        <div className="h-[200px] flex items-center">
          <FeedSpinner />
        </div>
      ) : (
        <div className={styles.wrapper}>
          {canScrollLeft && (
            <button
              type="button"
              className={`${styles.scrollButton} ${styles.scrollButtonLeft}`}
              onClick={() => scrollBy(-SCROLL_AMOUNT)}
              aria-label={`Scroll ${title} left`}
            >
              <ChevronLeft size={18} />
            </button>
          )}
          <div
            className={styles.scrollRow}
            ref={scrollRef}
            onScroll={updateScrollState}
          >
            {items?.map((item) => (
              <React.Fragment key={keyExtractor(item)}>
                {renderItem(item)}
              </React.Fragment>
            ))}
          </div>
          {canScrollRight && (
            <button
              type="button"
              className={`${styles.scrollButton} ${styles.scrollButtonRight}`}
              onClick={() => scrollBy(SCROLL_AMOUNT)}
              aria-label={`Scroll ${title} right`}
            >
              <ChevronRight size={18} />
            </button>
          )}
        </div>
      )}
    </div>
  )
}
